/**
 * Assessment intents — the client's WRITE half of the client data contract
 * (MYAC-930 §5). A mutation is a COMMAND DOC created under
 * `projects/{pid}/command_queues/assessment_intent/commands`; the backend flips
 * its status (`requested → accepted|rejected|blocked → running → done|failed`)
 * and the views push the visible result. The browser never calls the
 * assessments REST API for these.
 *
 * `sendAssessmentIntent` resolves at the ADMISSION decision (accepted /
 * rejected / blocked): admission is fast and is what gates the calling UI.
 * Execution can take minutes (a reassess job); its completion arrives through
 * `onOutcome` — and through the views themselves, which is why callers must
 * never block on it.
 *
 * Firestore rules constrain the create shape (type allowlist, `requested_by ==
 * auth.uid`, `requested_at == request.time`); everything else on the doc is
 * backend-written.
 *
 * One dev-only exception to "never calls the API": under `vite dev` with the
 * agents switch on `Local`, the intent is POSTed to the local command runner,
 * which writes the same doc with the Admin SDK. See `depositIntent`.
 */
import { addDoc, collection, doc, onSnapshot, serverTimestamp } from 'firebase/firestore'
import type { DocumentReference, Firestore } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { getAuth } from 'firebase/auth'
import { logger } from '@/utils/logger'
import { resolveLocalRunnerUrl } from '@/utils/agentEndpoint'
import { IntentAdmissionTimeout } from '@/composables/intentErrors'

export type AssessmentIntentType =
  | 'adopt'
  | 'remove'
  | 'set_mode'
  | 'reassess'
  | 'answer_clarification'
  | 'resume'
  //: S3 « Arrêter » — posts the scope abort fact inline; admissible while busy.
  | 'abort'
  | 'consistency'
  | 'remediation_edit'
  // MYAC-1008 — recompute the change-control prerequisites gate into
  // `views/change_control_status` (scope-level, free, inline). Rides this queue
  // because a queue is infrastructure; this is the platform's deterministic lane.
  | 'refresh_change_control_readiness'
  | 'refresh_registers_readiness'
  | 'refresh_audit_readiness'
  // MYAC-1107 — declare one scope-profile value from a settings surface: the
  // unsolicited sibling of `answer_clarification` (free, recorded inline,
  // then the replan wake).
  | 'set_profile_value'
  // MYAC-1051 — delete a standing playbook input (phase 1: evidence entries
  // only). Finishes inline like `remediation_edit`; the result carries
  // `changed` (bool), not a client-renderable message (see the CMS command).
  | 'playbook_delete'
  // MYAC-776 — the gap-analysis module's two command types (spec §5.1). They ride
  // this lane rather than owning one: it already has the job binary, the
  // transactional transitions and the scope-level serialisation an analysis needs
  // (spec §5, where the alternative is recorded and not chosen).
  | 'run_gap_analysis'
  | 'gap_analysis_act'
  // MYAC-776 — recompute the gap-analysis prerequisites gate into
  // `views/gap_analysis_status` (scope-level, free, inline), exactly as
  // `refresh_change_control_readiness` does for change control.
  | 'refresh_gap_analysis_readiness'
  // MYAC-1375 — manage a chat-authored document from the library: the chat's own
  // writes reached from a button. Inline and free; the library rows re-project.
  | 'authored_document_rename'
  | 'authored_document_set_status'
  | 'authored_document_delete'
  // MYAC-1522 (B3) — the person arrived: (re)write their welcome letter in this
  // company into `tenants/{tid}/views/welcome_letters/rows/{uid}` when its inputs
  // changed and the company has credits. Rides the lane of any project the person
  // belongs to there; fire and forget, the row arrives through its subscription.
  | 'refresh_welcome_letter'
  // MYAC-1612 — a person sets (or corrects) when one continuity row was last done.
  // Inline and free; the schedule and the menu badge re-project.
  | 'continuity_set_date'
  | 'continuity_find_date'

/** The backend's admission decision — what `sendAssessmentIntent` resolves with. */
export interface IntentAdmission {
  commandId: string
  /** `queued` (S3): admitted into the sequential FIFO — nothing is wrong,
   * the run starts at its mate's terminal; callers treat it as success.
   * `unanswered` (MYAC-1439): only with `admissionTimeoutMs: null` — the
   * outcome cap ran out while the doc was still not decided. Not a refusal:
   * callers say nothing (the page reads the doc itself). */
  status: 'accepted' | 'rejected' | 'blocked' | 'queued' | 'unanswered'
  /** i18n key (`referentials.intents.reason.*`) — set on rejected/blocked. */
  reasonKey?: string
}

/** A finished execution (only ever follows an `accepted` admission). */
export interface IntentOutcome {
  commandId: string
  status: 'done' | 'failed'
  reasonKey?: string
  result?: Record<string, unknown>
}

interface SendOptions {
  /** Fired once when an accepted command reaches `done`/`failed`. */
  onOutcome?: (outcome: IntentOutcome) => void
  /** Fired once the command doc is WRITTEN (before any admission decision) —
   *  for callers that close their UI on the deposit and keep the admission
   *  wait only for the refusal toast (MYAC-1439: a cold handler takes ~25 s). */
  onDeposited?: (commandId: string) => void
  /** Give up waiting for ADMISSION after this long (Eventarc hiccup, offline).
   *  `null` = NO admission clock (MYAC-1439): the promise settles only on a
   *  decision written on the doc, or silently (`unanswered`) at the outcome
   *  cap — a `requested` doc is never turned into a failure by time. */
  admissionTimeoutMs?: number | null
  /** Stop listening for the outcome after this long (job watchdog owns the rest). */
  outcomeTimeoutMs?: number
  firestore?: Firestore
}

const ADMISSION_TIMEOUT_MS = 30_000
/**
 * The outcome listener must OUTLIVE the run it watches (MYAC-968). At 30 minutes
 * it did not: a full referential runs 15-60 minutes, so a command that failed
 * late fell outside the window and the user got no error toast — the page simply
 * went quiet, which reads exactly like success.
 *
 * It is not a run budget and never was: progress and completion are read from
 * `views/assessment_status`, which the engine heartbeats at every action
 * boundary. This timer only bounds ONE single-doc `onSnapshot` whose sole job is
 * the failure toast, so the cost of being generous is a listener, and the cost of
 * being tight is a silent failure.
 */
const OUTCOME_TIMEOUT_MS = 90 * 60_000

const COMMANDS = (projectId: string) =>
  `projects/${projectId}/command_queues/assessment_intent/commands`

/**
 * Deposit the intent and return the command doc to listen on.
 *
 * Two deposits, ONE contract. In cloud mode the browser creates the doc and
 * Eventarc wakes the deployed handler. Under `vite dev` with the agents switch
 * on `Local`, the intent is POSTed to the local runner instead, which admits it
 * and creates the same doc — already concluded, so the cloud handler stands down
 * when Eventarc delivers the create. Either way what comes back is a reference to
 * the real command doc, and everything below this function is identical: the
 * admission verdict and the outcome arrive through the doc's snapshots, so local
 * mode exercises the production path rather than a parallel one.
 *
 * `resolveLocalRunnerUrl()` returns null in every deployed build (`import.meta
 * .env.DEV`), so this branch is dead code outside the dev server.
 */
async function depositIntent(
  db: Firestore,
  projectId: string,
  type: AssessmentIntentType,
  payload: Record<string, unknown>,
  uid: string,
): Promise<DocumentReference> {
  const runnerUrl = resolveLocalRunnerUrl()
  if (!runnerUrl) {
    return addDoc(collection(db, COMMANDS(projectId)), {
      type,
      payload,
      status: 'requested',
      requested_by: uid,
      requested_at: serverTimestamp(),
      command_version: 1,
    })
  }

  const res = await fetch(`${runnerUrl}/intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ project_id: projectId, type, payload, requested_by: uid }),
  })
  if (!res.ok) {
    // No doc was written, so no snapshot will ever resolve the promise — surface the
    // dev-setup problem (runner not started, wrong port) instead of timing out at 30s.
    throw new Error(`local runner ${runnerUrl}: ${res.status} ${await res.text()}`)
  }
  const body = (await res.json()) as { command_id: string; runner: string }
  logger.info(`intent ${type} ${body.command_id}: ran on ${body.runner}`)
  return doc(db, `${COMMANDS(projectId)}/${body.command_id}`)
}

export async function sendAssessmentIntent(
  projectId: string,
  type: AssessmentIntentType,
  payload: Record<string, unknown>,
  options: SendOptions = {},
): Promise<IntentAdmission> {
  const db = options.firestore ?? useFirestore()
  const uid = getAuth().currentUser?.uid
  if (!uid) throw new Error('not signed in')

  const ref = await depositIntent(db, projectId, type, payload, uid)
  options.onDeposited?.(ref.id)

  return new Promise<IntentAdmission>((resolve, reject) => {
    let admitted = false
    let stopped = false
    let unsubscribe = () => {}
    const stop = () => {
      stopped = true
      unsubscribe()
    }

    const admissionTimer =
      options.admissionTimeoutMs === null
        ? undefined
        : setTimeout(() => {
            if (admitted || stopped) return
            stop()
            // Typed, not a bare Error: this is the ONE rejection here whose cause the
            // client can honestly name, and `_intent`'s catch tells it apart from the
            // other three by class rather than by parsing this message (MYAC-1041).
            reject(new IntentAdmissionTimeout(`intent ${type} ${ref.id}: no admission decision`))
          }, options.admissionTimeoutMs ?? ADMISSION_TIMEOUT_MS)
    const outcomeTimer = setTimeout(() => {
      if (!stopped) stop()
      // No admission clock and still undecided: settle SILENTLY — a listener
      // bound, not a verdict on the command (MYAC-1439).
      if (!admitted) {
        admitted = true
        resolve({ commandId: ref.id, status: 'unanswered' })
      }
    }, options.outcomeTimeoutMs ?? OUTCOME_TIMEOUT_MS)
    const clearTimers = () => {
      clearTimeout(admissionTimer)
      clearTimeout(outcomeTimer)
    }

    unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const d = snap.data()
        if (!d) return
        const status = d.status as string
        // QUEUED IS AN ADMISSION (S3, lot 2 data): a sequential launch parks
        // as `requested` + `queued_behind` until its mate's terminal promotes
        // it — the old listener saw no admission there, timed out at 30 s and
        // toasted « Pas de réponse du serveur » on a perfectly healthy queue
        // (systematic on every multi-referential launch, live 2026-08-18).
        if (!admitted && status === 'requested' && (d.queued_behind as string)) {
          admitted = true
          clearTimeout(admissionTimer)
          resolve({ commandId: ref.id, status: 'queued' })
          if (!options.onOutcome) {
            clearTimers()
            stop()
          }
          return
        }
        if (!admitted && ['accepted', 'rejected', 'blocked'].includes(status)) {
          admitted = true
          clearTimeout(admissionTimer)
          resolve({
            commandId: ref.id,
            status: status as IntentAdmission['status'],
            reasonKey: (d.reason_key as string) || undefined,
          })
          // A denied command is terminal — nothing more will happen on the doc.
          if (status !== 'accepted' || !options.onOutcome) {
            clearTimers()
            stop()
          }
          return
        }
        if (['done', 'failed'].includes(status)) {
          clearTimers()
          stop()
          if (!admitted) {
            // BORN-TERMINAL (wake coalescing, 2026-08-07): a coalesced answer's
            // doc flips accepted→done in ~400ms, so the FIRST snapshot the
            // listener sees may already be terminal. Every `done` was
            // necessarily accepted; leaving the promise unsettled hung
            // `_intent`'s await and stuck `acting=true` — freezing every
            // question card on screen (live bug).
            admitted = true
            resolve({
              commandId: ref.id,
              status: status === 'done' ? 'accepted' : 'rejected',
              reasonKey: (d.reason_key as string) || undefined,
            })
          }
          options.onOutcome?.({
            commandId: ref.id,
            status: status as IntentOutcome['status'],
            reasonKey: (d.reason_key as string) || undefined,
            result: (d.result as Record<string, unknown>) || undefined,
          })
        }
      },
      (e) => {
        clearTimers()
        stop()
        if (!admitted) reject(e)
        else logger.error(`intent ${type} ${ref.id}: outcome listener failed`, e)
      },
    )
  })
}
